// ============================================================
// CharacteristicStrip - the nine characteristics as one row of cells.
//
// Each cell is the value, and under it the bonus the table actually
// plays with (the tens digit times the unnatural multiplier in force)
// beside the multiplier itself. A multiplier raised by a trait (Astartes
// doubles Strength and Toughness) shows in both, so the line reads true;
// typing a multiplier sets the sheet's own, which a trait only floors.
// ============================================================
import type { Character, CharacteristicId } from '../../engine/character/types';
import { characteristicBonus, getEffectiveUnnaturalMultipliers } from '../../engine/character/combatStatsResolver';
import { CHARACTERISTICS, characteristicPatch } from '../../engine/character/sheetPatches';
import ReadoutNumber from './ReadoutNumber';
import styles from './Sheet.module.css';

interface Props {
  character: Character;
  onChange: (partial: Partial<Character>) => void;
  disabled?: boolean;
  /** Only these, in catalog order: an Object shows its Toughness alone (2026-10-26). */
  only?: readonly CharacteristicId[];
}

export default function CharacteristicStrip({ character, onChange, disabled, only }: Props) {
  const effective = getEffectiveUnnaturalMultipliers(character);
  const set = (id: CharacteristicId, field: 'value' | 'unnaturalMultiplier', value: number) =>
    onChange(characteristicPatch(character, id, field, value));
  return (
    <div className={styles.strip}>
      {CHARACTERISTICS.filter(({ id }) => !only || only.includes(id)).map(({ id, short, name }) => {
        const stat = character.characteristics[id];
        const multiplier = effective[id] ?? stat.unnaturalMultiplier;
        const raised = multiplier > stat.unnaturalMultiplier;
        return (
          <div key={id} className={styles.cell} title={name} data-testid={`char-${id}`}>
            <span className={styles.cellKey}>{short}</span>
            <ReadoutNumber
              ariaLabel={name}
              size="large"
              min={0}
              max={200}
              value={stat.value}
              disabled={disabled}
              onChange={(value) => set(id, 'value', value)}
            />
            <div className={styles.cellBonus}>
              <span title={`${name} bonus`} data-testid={`char-bonus-${id}`}>
                {characteristicBonus(stat.value) * multiplier}
              </span>
              <span className={styles.cellMult}>
                ×
                <ReadoutNumber
                  ariaLabel={`Unnatural ${name}`}
                  size="small"
                  // A trait's multiplier is a floor: nothing below it can be typed.
                  min={multiplier > stat.unnaturalMultiplier ? multiplier : 1}
                  max={10}
                  // The multiplier IN FORCE, so the line reads true ("8 ×2")
                  // when a trait is what doubles it. Typing sets the sheet's own.
                  title={
                    raised ? `A trait sets ×${multiplier}; the sheet's own is ×${stat.unnaturalMultiplier}` : undefined
                  }
                  value={multiplier}
                  disabled={disabled}
                  onChange={(value) => set(id, 'unnaturalMultiplier', value)}
                />
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
